(() => {
  const ZONES = {
    green: {
      label: "Green zone (UK Mainland)",
      rates: {
        "0-2": "Free",
        "2-30": "Free",
        "30-70": "Free",
        "70-1000": "Free",
        "70-oversized": "Free",
      },
    },
    blue: {
      label: "Blue zone",
      rates: {
        "0-2": "Free",
        "2-30": "Free",
        "30-70": "£12.50",
        "70-1000": "£80.00",
        "70-oversized": "POA",
      },
    },
    orange: {
      label: "Orange zone",
      rates: {
        "0-2": "Free",
        "2-30": "£45.00",
        "30-70": "£70.00",
        "70-1000": "£120.00",
        "70-oversized": "POA",
      },
    },
    grey: {
      label: "Grey zone",
      rates: {
        "0-2": "POA",
        "2-30": "POA",
        "30-70": "POA",
        "70-1000": "POA",
        "70-oversized": "POA",
      },
    },
  };

  const BANDS = [
    { key: "0-2", label: "0–2kg" },
    { key: "2-30", label: "2–30kg" },
    { key: "30-70", label: "30–70kg" },
    { key: "70-1000", label: "70–1000kg" },
    {
      key: "70-oversized",
      label: "70kg+ & oversized (exceeds 1 full pallet)",
      help: "Max pallet size 1.2m (L) × 1.2m (W) × 2.29m (H), max 1000kg",
    },
  ];

  // Zone rules based on your map legend
  const RULES = {
    orange: [
      { area: "HS", ranges: [[1, 9]] },
      { area: "IV", ranges: [[41, 49], [55, 56]], singles: [51] },
      { area: "KA", ranges: [[27, 28]] },
      { area: "KW", ranges: [[15, 17]] },
      { area: "PA", ranges: [[41, 49], [60, 78]], singles: [20] },
      { area: "PH", ranges: [[42, 44]] },
      { area: "PO", ranges: [[30, 41]] },
      { area: "TR", ranges: [[21, 25]] },
      { area: "ZE", ranges: [[1, 3]] },
      // Channel Islands + Isle of Man
      { area: "GY", any: true },
      { area: "JE", any: true },
      { area: "IM", any: true },
    ],
    blue: [
      { area: "IV", ranges: [[4, 5], [13, 14], [21, 28], [52, 54]], singles: [63] },
      { area: "KW", ranges: [[1, 14]] },
      { area: "PA", ranges: [[21, 39]], singles: [80] },
      { area: "PH", ranges: [[19, 41], [49, 50]] },
    ],
    // grey: add later if you decide specific postcode groups are POA
  };

  function normalisePostcode(input) {
    let pc = (input || "").toUpperCase().trim();
    pc = pc.replace(/[^A-Z0-9 ]/g, "");
    pc = pc.replace(/\s+/g, " ");
    // If no space, attempt to split off last 3 chars (inward code)
    if (!pc.includes(" ") && pc.length > 4) {
      pc = pc.slice(0, -3) + " " + pc.slice(-3);
    }
    return pc;
  }

  function getOutcode(pc) {
    return pc.split(" ")[0]; // outward code
  }

  function parseAreaDistrict(outcode) {
    // e.g. IV41, PH49, SW1A, GY1
    const m = outcode.match(/^([A-Z]{1,2})(\d{1,2})([A-Z]?)$/);
    if (!m) return null;
    return {
      area: m[1],
      district: parseInt(m[2], 10),
      suffix: m[3] || "",
      raw: outcode,
    };
  }

  function matchesRule(parsed, rule) {
    if (!parsed || parsed.area !== rule.area) return false;
    if (rule.any) return true;

    const d = parsed.district;
    if (rule.singles && rule.singles.includes(d)) return true;

    if (rule.ranges) {
      for (const [min, max] of rule.ranges) {
        if (d >= min && d <= max) return true;
      }
    }
    return false;
  }

  function getZoneForPostcode(postcode) {
    const pc = normalisePostcode(postcode);
    const outcode = getOutcode(pc);
    const parsed = parseAreaDistrict(outcode);

    // If we can’t parse, treat as grey (POA) rather than guessing
    if (!parsed) return { zoneKey: "grey", pc, outcode };

    // Orange has priority over Blue where ranges border each other
    for (const rule of RULES.orange) {
      if (matchesRule(parsed, rule)) return { zoneKey: "orange", pc, outcode };
    }
    for (const rule of RULES.blue) {
      if (matchesRule(parsed, rule)) return { zoneKey: "blue", pc, outcode };
    }
    return { zoneKey: "green", pc, outcode };
  }

  function buildRatesTable(zoneKey) {
    const zone = ZONES[zoneKey];
    const rows = BANDS.map((b) => {
      const rate = zone.rates[b.key] ?? "POA";
      const help = b.help ? `<div class="pzc-help">${b.help}</div>` : "";
      return `
        <tr>
          <td>${b.label}${help}</td>
          <td>${rate}</td>
        </tr>
      `;
    }).join("");

    return `
      <table class="pzc-table" role="table" aria-label="Delivery charges by weight">
        <thead>
          <tr>
            <th scope="col">Gross weight (per product)</th>
            <th scope="col">Charge</th>
          </tr>
        </thead>
        <tbody>${rows}</tbody>
      </table>
    `;
  }

  function init() {
    const root = document.querySelector("[data-postcode-zone-checker]");
    if (!root) return;

    const form = root.querySelector("form");
    const input = root.querySelector("input[name='postcode']");
    const result = root.querySelector("[data-pzc-result]");
    const zoneEl = root.querySelector("[data-pzc-zone]");
    const tableEl = root.querySelector("[data-pzc-table]");
    const msgEl = root.querySelector("[data-pzc-message]");

    const show = (htmlZone, htmlTable, message) => {
      zoneEl.textContent = htmlZone;
      tableEl.innerHTML = htmlTable;
      msgEl.textContent = message;
      result.hidden = false;
      result.scrollIntoView({ behavior: "smooth", block: "nearest" });
    };

    form.addEventListener("submit", (e) => {
      e.preventDefault();
      const val = (input.value || "").trim();
      if (!val) {
        show("Please enter a postcode", "", "Enter your UK postcode to check your zone.");
        return;
      }

      const { zoneKey, pc, outcode } = getZoneForPostcode(val);
      const zoneLabel = ZONES[zoneKey].label;

      const message =
        zoneKey === "grey"
          ? `We couldn’t match “${pc}” to a delivery zone. Please contact us for a quote.`
          : `Postcode “${pc}” (outcode ${outcode}) is in the ${zoneLabel}.`;

      show(zoneLabel, buildRatesTable(zoneKey), message);
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();